import React, { useState, useEffect, useRef } from 'react';

const EditableField = ({ value, onSave, isMultiline = false, placeholder = '', type = 'text', isSelected = false, onSelect = null, isChanged = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (!isMultiline) {
        inputRef.current.select();
      }
    }
  }, [isEditing, isMultiline]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setEditValue(newValue);

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout for auto-save (2 seconds)
    timeoutRef.current = setTimeout(() => {
      saveValue(newValue);
    }, 2000);
  };

  const saveValue = async (newValue) => {
    if (newValue !== value) {
      setIsSaving(true);
      await onSave(newValue);
      setIsSaving(false);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    // Clear timeout if exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Save immediately on blur
    saveValue(editValue);
  };

  const handleKeyDown = (e) => {
    if (!isMultiline && e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    }
    if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const commonStyle = {
    fontSize: '14px',
    color: 'var(--text-primary)',
    background: 'transparent',
    border: 'none',
    padding: 0,
    width: '100%',
    fontFamily: 'inherit'
  };

  const editingStyle = {
    ...commonStyle,
    background: isSelected ? 'rgba(99, 102, 241, 0.2)' : isChanged ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
    border: isSelected ? '2px solid var(--indigo-500)' : isChanged ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid var(--indigo-500)',
    padding: '4px 8px',
    borderRadius: '4px',
    outline: 'none',
    boxShadow: isSelected ? '0 0 0 3px rgba(99, 102, 241, 0.2)' : 'none'
  };

  if (isEditing) {
    return (
      <div data-editable-field style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
        {isMultiline ? (
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              ...editingStyle,
              minHeight: '60px',
              resize: 'vertical'
            }}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={editingStyle}
          />
        )}
        {isSaving && (
          <span style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: 'var(--indigo-500)'
          }}>
            Saving...
          </span>
        )}
      </div>
    );
  }

  return (
    <span
      data-editable-field
      onClick={(e) => {
        if (onSelect) {
          onSelect();
          e.stopPropagation();
        } else {
          setIsEditing(true);
        }
      }}
      onDoubleClick={() => setIsEditing(true)}
      style={{
        ...commonStyle,
        cursor: 'pointer',
        borderBottom: isSelected ? '2px solid var(--indigo-500)' : isChanged ? '1px solid rgba(16, 185, 129, 0.3)' : '1px dashed rgba(255, 255, 255, 0.2)',
        background: isSelected ? 'rgba(147, 51, 234, 0.15)' : isChanged ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
        padding: (isSelected || isChanged) ? '4px 8px' : '0',
        borderRadius: (isSelected || isChanged) ? '4px' : '0',
        boxShadow: isSelected ? '0 0 0 3px rgba(99, 102, 241, 0.2)' : 'none',
        transition: 'all 0.2s',
        display: 'inline-block',
        minWidth: '50px'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.target.style.borderBottom = '1px dashed var(--indigo-500)';
          e.target.style.color = 'var(--indigo-500)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.target.style.borderBottom = '1px dashed rgba(255, 255, 255, 0.2)';
          e.target.style.color = '#fff';
        }
      }}
    >
      {value || placeholder}
    </span>
  );
};

export default EditableField;
